import React, { ReactElement } from "react";
import {
  Avatar,
  Button,
  Col,
  Collapse,
  Form,
  Input,
  Row,
  Typography,
  Upload,
} from "antd";
import {
  CloudUploadOutlined,
  DeleteOutlined,
  FileImageOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import DocIcon from "../../Atoms/images/file-icon/doc_icon.png";
import PdfIcon from "../../Atoms/images/file-icon/pdf_icon.png";
import XlsIcon from "../../Atoms/images/file-icon/xls_icon.png";
import { useUploadFile } from "./api";
import { UploadFile } from "antd/es/upload/interface";
import styled from "@emotion/styled";
import { Rule } from "antd/lib/form";

const { Panel } = Collapse;

export interface File {
  fileName: string;
  url?: string;
  size: number;
  mimetype: string;
  uuid: string;
}

interface UploadDocProps {
  title: string;
  name?: string;
  remarkName?: string;
  type?: string;
  required?: boolean;
  hints?: boolean;
  preferable?: boolean;
  remarkDisabled?: boolean;
  uploadDisabled?: boolean;
  saveDisabled?: boolean;
  danger?: boolean;
  reviewerRemark?: string;
  rules?: Rule[];
}

export function UploadDoc(props: UploadDocProps) {
  const { mutate: upload } = useUploadFile();

  const cusUpload = async (options: any) => {
    const { onSuccess, onError, file, onProgress } = options;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res: File = await upload(formData);
      onSuccess(res);
    } catch (err) {
      const error = new Error("Some error");
      onError({ err });
    }
  };

  const uploadProps = {
    name: "files",
    multiple: true,
    showRemoveIcon: !props.saveDisabled,
    customRequest: cusUpload,
  };

  const docProps = {
    customRequest: cusUpload,
    multiple: true,
    name: "files",
    showRemoveIcon: !props.saveDisabled,
    itemRender: (
      originNode: ReactElement,
      file: UploadFile,
      fileList: object[],
      actions: any
    ) => {
      const patternFileExtension = /\.([0-9a-z]+)(?:[#]|$)/i;
      const extension =
        (file.fileName
          ? file.fileName.match(patternFileExtension)
          : file.name.match(patternFileExtension)) || [];

      let icon = null;
      switch (extension[1]) {
        case "doc":
        case "docx":
          icon = DocIcon;
          break;
        case "pdf":
          icon = PdfIcon;
          break;
        case "xls":
        case "xlsx":
          icon = XlsIcon;
          break;
        default:
          icon = (
            <FileImageOutlined style={{ color: "#F98001", fontSize: 22 }} />
          );
      }

      return (
        <Row align="middle">
          <Col flex={"none"}>
            <Avatar
              shape="square"
              size="small"
              style={{ margin: "16px 10px 5px 0px" }}
              src={icon}
            />
          </Col>
          <Col flex={`none`}>
            <div
              style={{ marginTop: 12, cursor: "pointer" }}
              onClick={() => actions.download()}
            >
              {file.name || file.fileName}
            </div>
          </Col>
          {!props.saveDisabled && (
            <Col flex={`auto`}>
              <Button
                style={{ marginTop: 10 }}
                type={"link"}
                shape="circle"
                icon={<DeleteOutlined />}
                onClick={() => actions.remove()}
              />
            </Col>
          )}
        </Row>
      );
    },
  };

  const normFile = (e: any) => {
    if (e.file.status !== "done") {
      return e && e.fileList;
    }

    const temp = e.fileList.map((item: any) => {
      if (item.response) {
        return {
          uuid: item.response.uuid,
          docType: props.name,
          ...item,
        };
      }
      return item;
    });

    return temp;
  };

  return (
    <Collapse
      ghost
      expandIconPosition={`right`}
      style={{ width: "100%", padding: 0 }}
    >
      <Panel
        header={
          <StyledHeader danger={props.danger}>
            {props.title}
            {props.required && (
              <RequireInfo>This section is required.</RequireInfo>
            )}
            {props.hints && (
              <RequireInfo>This section is required.</RequireInfo>
            )}
            {props.preferable && (
              <RequireInfo>This section is preferable.</RequireInfo>
            )}
            {props.reviewerRemark && (
              <ReviewRemark>
                Remark from reviewer - {props.reviewerRemark}
              </ReviewRemark>
            )}
          </StyledHeader>
        }
        key="1"
      >
        <Row>
          <Col lg={12}>
            {props.type && (
              <Form.Item label="">
                <Form.Item
                  name={props.name}
                  valuePropName="fileList"
                  getValueFromEvent={normFile}
                  noStyle
                  rules={props.rules}
                >
                  {props.type && props.type === "drag" ? (
                    <Upload.Dragger
                      disabled={props.uploadDisabled}
                      listType="picture-card"
                      {...uploadProps}
                    >
                      <p className="ant-upload-drag-icon">
                        <CloudUploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Drop your files here, or Browse
                      </p>
                      <p className="ant-upload-hint">Support: JPG, JPEG, PNG</p>
                    </Upload.Dragger>
                  ) : (
                    <Upload {...docProps} listType="picture">
                      <Button
                        disabled={props.uploadDisabled}
                        type={`primary`}
                        icon={<UploadOutlined />}
                      >
                        Upload document
                      </Button>
                    </Upload>
                  )}
                </Form.Item>
              </Form.Item>
            )}
            <div>Remarks</div>
            <Form.Item label="" name={props.remarkName} rules={props.rules}>
              <Input.TextArea
                disabled={props.remarkDisabled}
                autoSize={{ minRows: 5, maxRows: 5 }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Panel>
    </Collapse>
  );
}
const Header = ({ danger, children, ...props }: any) => {
  return <h2 {...props}>{children}</h2>;
};

const StyledHeader = styled(Header)`
  color: ${(props) => (props.danger ? "#ED4646" : " #000000")};
  > div:last-child {
    color: ${(props) => (props.danger ? "#ED4646" : " #000000")};
  }
`;
const RequireInfo = styled.div`
  color: #ff5555 !important;
  font-size: 12px;
`;

const ReviewRemark = styled(Typography.Paragraph)`
  font-size: 12px;
`;
